import classnames from 'classnames';
import { useLocale, useTranslations } from 'next-intl';
import React from 'react';

import TwoButtonComponent from '@/components/button/TwoButtonComponent';

import { WEBAPP_URL } from '@/constants/http-constants';

import { addUtmParams } from '@/helpers/utm-helper';
import { stringFormatJsx } from '@cineamo/ui/src/helpers/string-helper';

import useCookies from '@/hooks/useCookies';

import { IconLock16 } from '@cineamo/ui/src/icons/ic-lock';

import Modal, { ModalProps } from './Modal';

type CookiesModalProps = ModalProps & {
    onShowCookiePreferences: () => void;
};

export function CookiesModal({ onShowCookiePreferences, ...props }: CookiesModalProps) {
    const t = useTranslations();
    const locale = useLocale();

    const domainConfiguration = props.domainConfiguration;

    const { acceptAllOptionalCookies, rejectAllOptionalCookies } = useCookies();

    function acceptAllOptionalCookiesAndClose() {
        acceptAllOptionalCookies();
        props.setModalIsOpen?.(false);
    }

    function rejectAllOptionalCookiesAndClose() {
        rejectAllOptionalCookies();
        props.setModalIsOpen?.(false);
    }

    return (
        <Modal
            {...props}
            onClose={null}
            className="!mt-auto"
            showBackground={false}
            // Blurred backdrop as on the web app, whose cookie banner leaves this at its default.
            // The page stays operable — `enableOutsideActions` keeps the backdrop click-through —
            // but the blur makes clear that a decision is pending.
            showCloseButton={false}
            closeable={false}
            enableOutsideActions>
            <div className="flex flex-col lg:flex-row flex-auto lg:space-x-24 space-y-16 lg:space-y-0">
                <div className="flex flex-col flex-auto lg:w-1/2 space-y-6">
                    <div className="flex flex-row space-x-12 items-center">
                        <span className="text-body-bold-16">{t('common.cookies-modal.your-privacy')}</span>
                        <IconLock16
                            className="flex-none"
                            style={
                                domainConfiguration
                                    ? {
                                          color: domainConfiguration.style.primaryColor,
                                          filter: 'brightness(80%)'
                                      }
                                    : undefined
                            }
                        />
                    </div>
                    <span className="text-body-12">
                        {stringFormatJsx(
                            t('common.cookies-modal.description', { 0: '{0}' }),
                            <a
                                className="underline"
                                href={addUtmParams(`${WEBAPP_URL}/data-protection?locale=${locale}`, {
                                    utmSource: 'cinema_website',
                                    utmContent: 'cookies_modal-data_protection',
                                    utmCampaign: `cinema_${domainConfiguration.cinema.id}-${domainConfiguration.cinema.name}`,
                                    utmMedium: 'website'
                                })}
                                target="_blank">
                                {t('common.cookies-modal.privacy-policy')}
                            </a>
                        )}
                    </span>
                </div>
                <div className="flex flex-col lg:w-1/2 lg:justify-center space-y-16">
                    <TwoButtonComponent
                        primaryLabel={t('common.cookies-modal.accept')}
                        primaryAction={acceptAllOptionalCookiesAndClose}
                        primaryButtonProps={{
                            className: classnames('h-48'),
                            css: domainConfiguration
                                ? {
                                      backgroundColor: domainConfiguration.style.primaryColor,
                                      color: domainConfiguration.style.onPrimaryColor
                                  }
                                : undefined
                        }}
                        secondaryLabel={t('common.cookies-modal.cookie-preferences')}
                        secondaryAction={onShowCookiePreferences}
                        secondaryButtonProps={{
                            className: classnames('h-48'),
                            css: domainConfiguration
                                ? {
                                      borderColor: domainConfiguration.style.primaryColor,
                                      color: domainConfiguration.style.primaryColor
                                  }
                                : undefined
                        }}
                        stretched
                    />
                    <span
                        className="flex clickable-element underline text-body-12"
                        onClick={rejectAllOptionalCookiesAndClose}>
                        {t('common.cookies-modal.decline-and-continue')}
                    </span>
                </div>
            </div>
        </Modal>
    );
}
